/**
 * Meta provider is responsibile for keeping state of market meta information results such as filters
 *
 */
import { createContext, ReactNode, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import useSearch from 'hooks/useSearch';
import { DEFAULT_PAGINATION_OPTION } from 'utils/market';
import { AllFilterPropsFragment as MarketFilters, FilterType, useInputsMarketFiltersQuery } from 'generated/graphql';
import useInputsMarketQueryPaused from '../hooks/useInputsMarketQueryPaused';
import { useInputsState } from './InputsStateProvider';

interface InputsFilterContextProps {
  filters?: MarketFilters;
  isFetching: boolean;
  title?: string;
}

export const Context = createContext<InputsFilterContextProps | undefined>(undefined);

export const InputsFilterProvider = ({ children }: { children: ReactNode }) => {
  const { queryInput, filtersState } = useInputsState();
  const location = useLocation();
  const isQuotesRoute = useMemo(() => location.pathname.includes('/quotes'), [location.pathname]);

  const [getParam, , removeParam] = useSearch();
  const typeID = getParam('typeID');
  const sectionID = getParam('sectionID');
  const filterType = getParam('filterType');

  /* Handle all URL filters together */
  useEffect(() => {
    const newFilters = [];

    // Handle VolumeAdjustment filter type
    if (filterType && filterType === 'VolumeAdjustment') {
      newFilters.push({
        type: FilterType.VolumeAdjustment,
        selectedIDs: [],
      });
    }

    // Handle filter by section
    if (sectionID && sectionID.length > 0) {
      newFilters.push({
        type: FilterType.Section,
        selectedIDs: [sectionID],
      });
    }

    // Handle filter by type
    if (typeID && typeID.length > 0) {
      newFilters.push({
        type: FilterType.Type,
        selectedIDs: [typeID],
      });
    }

    // If any filters were added, update state and remove params
    if (newFilters.length > 0) {
      filtersState.setSelectedFilters(newFilters);

      // Remove all relevant params at once (pass a function to removeParam if it supports it, or chain deletions)
      if (filterType && filterType === 'VolumeAdjustment') {
        removeParam('filterType');
      }
      if (sectionID && sectionID.length > 0) {
        removeParam('sectionID');
      }
      if (typeID && typeID.length > 0) {
        removeParam('typeID');
      }
    }
  }, [typeID, sectionID, filterType, filtersState, removeParam]);

  const isQueryPaused = useInputsMarketQueryPaused();
  const [marketResult, refreshMarketResult] = useInputsMarketFiltersQuery({
    variables: {
      input: {
        search: '',
        filters: queryInput.filters,
        buyerID: queryInput.buyerID,
        paginationOption: DEFAULT_PAGINATION_OPTION,
      },
    },
    pause: isQueryPaused && !isQuotesRoute,
  });

  const value = useMemo(
    () => ({
      filters: marketResult?.data?.inputsMarket?.filters,
      isFetching: marketResult.fetching,
      title: marketResult?.data?.inputsMarket.title,
    }),
    [marketResult?.data?.inputsMarket?.filters, marketResult?.fetching, marketResult?.data?.inputsMarket?.title],
  );

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

export const useInputsFilter = () => {
  const result = useContext(Context);
  if (!result) throw new Error('useInputsFilter must be used within a InputsFilterProvider');
  return result;
};
